// partnerErfassen.ts — „Verheiratet" gewählt, aber kein Ehepartner erfasst?
// (Themen 01.10.2026, T3)
//
// Wählt man in Anlage oder Akte „Verheiratet" (bzw. „Zusammenlebend") und es
// steht noch kein lebender Ehepartner (Lebenspartner) in den Beziehungen,
// fragt ein eigenes Popup, ob er gleich erfasst werden soll. Das Label folgt
// dem Geschlecht DIESES Kontakts — es sagt, was die andere Person für ihn ist:
// Mann → „Ehefrau", Frau → „Ehemann". Ohne Geschlecht wird nicht geraten,
// das Popup bietet beide an. Im Beziehungsdialog bleibt das Label änderbar.
//
// Seit 02.10.2026 analog „Geschieden" → „Ex-Partner erfassen" (Mann →
// „Ex-Partnerin", Frau → „Ex-Partner"), aber NICHT im Haushalt, sondern unter
// „Beziehungen außerhalb des Haushaltes".
//
// Überspringbar: Der Familienstand ist dann trotzdem gewählt.

import { auswahl } from "../redesign/bestaetigen";
import { partnerArtVonLabel } from "../../utils/familienstandAbleiten";

export type PartnerFrageArt = "ehe" | "lebenspartner" | "ex";

/** Zu welchem Familienstand gehört welche Frage? */
export function partnerFrageFuerStatus(status: string | null | undefined): PartnerFrageArt | null {
  const s = String(status ?? "").trim().toLowerCase();
  if (s === "verheiratet") return "ehe";
  if (s === "zusammenlebend") return "lebenspartner";
  // „Wenn geschieden, dann Ex-Partner — das ist eine Beziehung bei uns“ (User 02.10.2026).
  if (s === "geschieden") return "ex";
  return null;
}

/**
 * Gehört der Partner in den Haushalt? Ehe- und Lebenspartner ja, ein
 * Ex-Partner nicht — er steht unter „Beziehungen außerhalb des Haushaltes“.
 */
export const partnerImHaushalt = (art: PartnerFrageArt): boolean => art !== "ex";

const LABELS: Record<PartnerFrageArt, [fuerMann: string, fuerFrau: string]> = {
  ehe: ["Ehefrau", "Ehemann"],
  lebenspartner: ["Lebenspartnerin", "Lebenspartner"],
  ex: ["Ex-Partnerin", "Ex-Partner"],
};

const TEXTE: Record<PartnerFrageArt, { titel: string; wer: string; wo: string }> = {
  ehe: { titel: "Ehepartner erfassen", wer: "kein Ehepartner", wo: "im Haushalt" },
  lebenspartner: { titel: "Lebenspartner erfassen", wer: "kein Lebenspartner", wo: "im Haushalt" },
  ex: { titel: "Ex-Partner erfassen", wer: "kein Ex-Partner", wo: "unter „Beziehungen außerhalb des Haushaltes“" },
};

/** Die anzubietenden Labels — „was die andere Person für diesen Kontakt ist". */
export function partnerLabelOptionen(art: PartnerFrageArt, geschlecht: "m" | "f" | null): string[] {
  const [fuerMann, fuerFrau] = LABELS[art];
  if (geschlecht === "m") return [fuerMann];
  if (geschlecht === "f") return [fuerFrau];
  return [fuerMann, fuerFrau];
}

/**
 * Steht schon ein passender Partner in den Beziehungen? Bei Ehe- und
 * Lebenspartnern zählen nur LEBENDE — wer nach dem Tod der Ehefrau wieder
 * heiratet, wird gefragt. Ein Ex-Partner zählt auch, wenn er verstorben ist
 * (Ex-Ehemann/-Ehefrau sind ebenfalls Ex-Partner).
 */
export function hatLebendenPartner(
  art: PartnerFrageArt,
  beziehungen: ReadonlyArray<{ label: string | null | undefined; verstorben?: boolean }>,
): boolean {
  return beziehungen.some((b) => partnerArtVonLabel(b.label) === art && (art === "ex" || !b.verstorben));
}

/**
 * Das Popup. Liefert das gewählte Label oder `null` (überspringen).
 */
export function partnerErfassenFragen(art: PartnerFrageArt, geschlecht: "m" | "f" | null): Promise<string | null> {
  const t = TEXTE[art];
  const optionen = partnerLabelOptionen(art, geschlecht);
  return auswahl({
    titel: t.titel,
    text:
      `Es ist noch ${t.wer} erfasst. Jetzt ${t.wo} hinzufügen?` +
      (optionen.length === 1 ? `\nBeziehung: ${optionen[0]} (im nächsten Schritt änderbar)` : ""),
    label: "Beziehung",
    optionen,
    ja: t.titel,
    nein: "Überspringen",
  });
}
